import { ChangeDetectionStrategy, Component, computed, ElementRef, HostListener, inject, signal, viewChild } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { SafeResourceUrlPipe } from '../../pipes-common/pipes';
import { IrisGlobalSandbox } from '@iris/common/redux/global.sandbox';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { finalize, first, switchMap, take, tap } from 'rxjs';
import { IrisMsalService } from '@iris/modules/msal/msal.service';
import { IrisLoadingButtonDirective } from '../../directives-common/directives/iris-loading-button.directive';
import { IrisFoldersService } from '@iris/common/services/docs/folders.service';
import { IrisSiaService } from '../sia.service';
import { distinctUntilPropertyChanged } from '@iris/common/utils/rxjs.utils';
import { toSignal } from '@angular/core/rxjs-interop';
import { IrisDMSFileI } from '../../dms/models/IrisDMSFile';
import { StrakitButtonComponent } from '@ngx-strakit/cdk/controls';
import { IrisSimpleLoaderModule } from '../../iris-simple-loader/simple-loader.module';
import { IrisEmptyContentViewComponent } from '../../dms/components/empty-content-view/empty-content-view.component';
import {
  ConfirmByKeypressDirective,
  SkModalBase,
  SkModalContentComponent,
  StrakitButtonPrefixDirective,
} from '@ngx-strakit/cdk';

interface IrisSiaImageMessageI {
  type: 'SIA_IFRAME_IMAGES_MESSAGE';
  message: string[];
}

interface IrisSiaImageCountMessageI {
  type: 'SIA_IFRAME_IMAGE_COUNT_MESSAGE';
  message: number;
}

interface IrisSIaIframeLoadingMessageI {
  type: 'iFrameReady' | 'tokenRefreshRequest';
}

export type IrisSiaIframeMessageI = IrisSiaImageMessageI | IrisSiaImageCountMessageI | IrisSIaIframeLoadingMessageI;
@Component({
  selector: 'iris-sia-file-picker',
  imports: [
    TranslatePipe,
    SkModalContentComponent,
    ConfirmByKeypressDirective,
    SafeResourceUrlPipe,
    IrisLoadingButtonDirective,
    StrakitButtonComponent,
    StrakitButtonPrefixDirective,
    IrisSimpleLoaderModule,
    IrisEmptyContentViewComponent,
  ],
  templateUrl: './sia-file-picker.component.html',
  styleUrl: './sia-file-picker.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class IrisSiaFilePickerComponent extends SkModalBase<IrisSiaFilePickerComponent, IrisDMSFileI[]> {
  private readonly globalSandbox = inject(IrisGlobalSandbox);
  private readonly msalService = inject(IrisMsalService);
  private readonly folderService = inject(IrisFoldersService);
  private readonly siaService = inject(IrisSiaService);
  private readonly data = inject<{ folderId: string; maxSelectedPhotos: number }>(MAT_DIALOG_DATA);

  readonly selectedFilesUploading = signal(false);
  readonly selectedFilesCount = signal(0);
  readonly hasSelectedFiles = computed(() => !!this.selectedFilesCount());

  private readonly siaUrl$ = this.globalSandbox.currentProject$.pipe(
    distinctUntilPropertyChanged('strathekNumber'),
    switchMap(project => this.siaService.generateSiaUrl({ strathekNumber: project.strathekNumber, maxSelectedPhotos: this.data.maxSelectedPhotos }).pipe(first())),
  );

  readonly siaUrl = toSignal(this.siaUrl$);

  readonly siaIframe = viewChild<ElementRef<HTMLIFrameElement>>('siaIframe');
  readonly iframeWindow = computed(() => this.siaIframe()?.nativeElement?.contentWindow);

  private readonly iframeReady = signal(false);

  onSelect(): void {
    this.selectedFilesUploading.set(true);
    this.iframeWindow().postMessage({
      type: 'selectedImagesRequest',
    }, this.siaService.allowedDomain());
  }

  @HostListener('window:message', ['$event'])
  protected handleSiaMessages(event: MessageEvent<IrisSiaIframeMessageI>): void {
    if (!this.siaService.isDomainAllowed(event.origin)) return;

    if (event.data.type === 'iFrameReady') {
      this.iframeReady.set(true);
      this.sendAccessToken();
      return;
    }

    if (event.data.type === 'tokenRefreshRequest') {
      this.sendAccessToken(true);
      return;
    }

    if (event.data.type === 'SIA_IFRAME_IMAGE_COUNT_MESSAGE') {
      this.selectedFilesCount.set(event.data.message ?? 0);
      return;
    }

    // Handle selected images message
    if (event.data.type === 'SIA_IFRAME_IMAGES_MESSAGE') {
      this.processSelectedImagesAndClose(event.data.message);
      return;
    }
  }

  private processSelectedImagesAndClose(selectedFiles: string[]): void {
    this.folderService.uploadSiaFiles(this.data.folderId, selectedFiles).pipe(
      take(1),
      tap(files => this.closeWithResult(files)),
      finalize(() => this.selectedFilesUploading.set(false)),
    ).subscribe();
  }

  private sendAccessToken(refresh = false): void {
    if (!this.iframeReady()) {
      alert('iframe not ready yet');
      return;
    }
    this.msalService.acquireTokenSilent({ forceRefresh: refresh }).pipe(
      take(1),
      tap(() => {
        const activeAccount = this.msalService.checkAndSetActiveAccount();
        this.postAccessTokenToIframe(activeAccount.idToken);
      }),
    ).subscribe();
  }

  private postAccessTokenToIframe(token: string): void {
    this.iframeWindow().postMessage({
      type: 'iFrameAccessToken',
      token,
    }, this.siaService.allowedDomain());
  }
}
